import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    Alert, Box, Button, Chip, CircularProgress, FormControlLabel, IconButton, MenuItem, Paper, Switch,
    TextField, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { AuthService } from '../../services/auth.service';
import { MaintenanceService } from '../../services/maintenance.service';
import { DepartmentService } from '../../services/department.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { PERMISSIONS } from '../../constants/permissions';
import SearchableSelect from '../common/SearchableSelect';
import {
    CALENDAR_UNITS, CalendarUnit, EquipmentDTO, EquipmentTypeDTO, MeterDTO, PM_BASES, PM_TRIGGERS, PmBasis,
    PmBatchEditRequest, PmBulkResult, PmPlanDTO, PmPlanFields, PmTrigger, TechnicianDTO, WORK_ORDER_PRIORITIES, WorkOrderPriority, mntLabel,
} from '../../interfaces/maintenance';
import { Department } from '../../interfaces/department.interface';
import { PM_STATE_COLOR, PM_STATE_LABEL, pmDueHint, todayLocal } from './pmCommon';

const field = { flex: '1 1 220px', maxWidth: 380, minWidth: 0 };

/**
 * Create / edit one PM plan, or (new plans only, "Apply to many") one plan template stamped onto every machine of a
 * type and/or location, with a preview count first.
 */
const PmPlanForm: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [params] = useSearchParams();
    const isEdit = !!id;
    const navigate = useNavigate();
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.MAINTENANCE_PM_MANAGE);
    const institutionId = AuthService.getSelectedInstitution()!;

    const [many, setMany] = useState(!isEdit && params.get('mode') === 'many');
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [plan, setPlan] = useState<PmPlanDTO | null>(null);
    const [technicians, setTechnicians] = useState<TechnicianDTO[]>([]);
    const [equipment, setEquipment] = useState<EquipmentDTO[]>([]);
    const [meters, setMeters] = useState<MeterDTO[]>([]);
    const [types, setTypes] = useState<EquipmentTypeDTO[]>([]);
    const [departments, setDepartments] = useState<Department[]>([]);

    const [equipmentId, setEquipmentId] = useState('');
    const [name, setName] = useState('');
    const [trigger, setTrigger] = useState<PmTrigger>('CALENDAR');
    const [basis, setBasis] = useState<PmBasis>('FLOATING');
    const [everyCount, setEveryCount] = useState('1');
    const [everyUnit, setEveryUnit] = useState<CalendarUnit>('MONTH');
    const [meterId, setMeterId] = useState('');
    const [meterCode, setMeterCode] = useState('');
    const [everyMeter, setEveryMeter] = useState('');
    const [leadDays, setLeadDays] = useState('0');
    const [leadMeter, setLeadMeter] = useState('');
    const [startDate, setStartDate] = useState(todayLocal());
    const [assignee, setAssignee] = useState('');
    const [priority, setPriority] = useState<WorkOrderPriority>('NORMAL');
    const [estimatedMinutes, setEstimatedMinutes] = useState('');
    const [active, setActive] = useState(true);
    const [tasks, setTasks] = useState<string[]>(['']);

    const [typeId, setTypeId] = useState('');
    const [locationId, setLocationId] = useState('');
    const [spreadDays, setSpreadDays] = useState('0');
    const [preview, setPreview] = useState<PmBulkResult | null>(null);

    // batch edit (plans created together by "Apply to many")
    const [batchActive, setBatchActive] = useState('');
    const [batchPriority, setBatchPriority] = useState('');
    const [batchLeadDays, setBatchLeadDays] = useState('');
    const [batchAssignee, setBatchAssignee] = useState('');
    const [batchTasks, setBatchTasks] = useState(false);
    const [batchSaving, setBatchSaving] = useState(false);

    // the editing machine may not be in the first page of the list: seed it from the plan
    const equipmentOptions = React.useMemo<EquipmentDTO[]>(() => {
        if (!plan || equipment.some(e => e.id === plan.equipmentId)) return equipment;
        return [{ id: plan.equipmentId, code: plan.equipmentCode, name: plan.equipmentName } as EquipmentDTO, ...equipment];
    }, [equipment, plan]);

    const usesCalendar = trigger !== 'METER';
    const usesMeter = trigger !== 'CALENDAR';

    useEffect(() => {
        MaintenanceService.listTechnicians(institutionId).then(setTechnicians).catch(() => setTechnicians([]));
        MaintenanceService.listEquipment(institutionId, {}, 0, 50).then(r => setEquipment(r.content)).catch(() => setEquipment([]));
        MaintenanceService.listTypes(institutionId).then(t => setTypes(t.filter(x => x.active))).catch(() => setTypes([]));
        DepartmentService.getLocationOptions().then(r => setDepartments(r.content ?? [])).catch(() => setDepartments([]));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!id) return;
        MaintenanceService.getPmPlan(id).then(p => {
            setPlan(p);
            setEquipmentId(p.equipmentId); setName(p.name); setTrigger(p.trigger); setBasis(p.basis);
            setEveryCount(p.everyCount ? String(p.everyCount) : '1'); setEveryUnit(p.everyUnit ?? 'MONTH');
            setMeterId(p.meterId ?? ''); setEveryMeter(p.everyMeter != null ? String(p.everyMeter) : '');
            setLeadDays(String(p.leadDays)); setLeadMeter(p.leadMeter != null ? String(p.leadMeter) : '');
            setStartDate(p.startDate); setAssignee(p.defaultAssignee ?? ''); setPriority(p.priority);
            setEstimatedMinutes(p.estimatedMinutes != null ? String(p.estimatedMinutes) : '');
            setActive(p.active); setTasks(p.tasks && p.tasks.length ? p.tasks : ['']);
        }).catch(e => toast.showApiError(e, 'Failed to load the PM plan')).finally(() => setLoading(false));
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    // PM-driver meters of the chosen machine (single mode).
    useEffect(() => {
        if (many || !equipmentId) { setMeters([]); return; }
        MaintenanceService.getEquipment(equipmentId)
            .then(e => setMeters((e.meters ?? []).filter(m => m.isPmDriver && m.active)))
            .catch(() => setMeters([]));
    }, [equipmentId, many]);

    const searchEquipment = (text: string) => {
        MaintenanceService.listEquipment(institutionId, { q: text }, 0, 50).then(r => setEquipment(r.content)).catch(() => undefined);
    };

    const num = (v: string): number | null => (v.trim() === '' || Number.isNaN(Number(v)) ? null : Number(v));

    const buildFields = (): PmPlanFields => ({
        name: name.trim(), trigger, basis,
        everyCount: usesCalendar ? num(everyCount) : null,
        everyUnit: usesCalendar ? everyUnit : null,
        everyMeter: usesMeter ? num(everyMeter) : null,
        leadDays: num(leadDays) ?? 0,
        leadMeter: usesMeter ? num(leadMeter) : null,
        startDate, defaultAssignee: assignee || null, priority,
        estimatedMinutes: num(estimatedMinutes),
        tasks: tasks.map(t => t.trim()).filter(t => t !== ''),
    });

    const validate = (): string | null => {
        if (!name.trim()) return 'Name is required';
        if (!startDate) return 'Start date is required';
        if (usesCalendar && (!num(everyCount) || num(everyCount)! < 1)) return 'Enter how often the plan repeats';
        if (usesMeter && (!num(everyMeter) || num(everyMeter)! <= 0)) return 'Enter the meter interval';
        if (!many && !equipmentId) return 'Choose a machine';
        if (!many && usesMeter && !meterId) return 'Choose the PM-driver meter';
        if (many && usesMeter && !meterCode.trim()) return 'Enter the meter code to use on each machine';
        return null;
    };

    const bulkBody = (previewOnly: boolean) => ({
        template: { ...buildFields(), meterCode: usesMeter ? meterCode.trim() : null },
        equipmentFilter: { equipmentTypeId: typeId || null, locationDepartmentId: locationId || null },
        spreadDays: num(spreadDays) ?? 0,
        preview: previewOnly,
    });

    const runPreview = async () => {
        const err = validate();
        if (err) { toast.showError(err); return; }
        try {
            setSaving(true);
            setPreview(await MaintenanceService.bulkPmPlans(institutionId, bulkBody(true)));
        } catch (e) {
            toast.showApiError(e, 'Failed to preview');
        } finally {
            setSaving(false);
        }
    };

    const handleSave = async () => {
        const err = validate();
        if (err) { toast.showError(err); return; }
        try {
            setSaving(true);
            if (many) {
                const r = await MaintenanceService.bulkPmPlans(institutionId, bulkBody(false));
                toast.showSuccess(`${r.created} plan(s) created, ${r.skipped} skipped`);
                navigate('/maintenance/pm-plans');
            } else if (isEdit) {
                await MaintenanceService.updatePmPlan(id!, { ...buildFields(), meterId: usesMeter ? meterId : null, active });
                toast.showSuccess('PM plan saved');
                navigate('/maintenance/pm-plans');
            } else {
                await MaintenanceService.createPmPlan({ ...buildFields(), institutionId, equipmentId, meterId: usesMeter ? meterId : null });
                toast.showSuccess('PM plan created');
                navigate('/maintenance/pm-plans');
            }
        } catch (e) {
            toast.showApiError(e, 'Failed to save the PM plan');
        } finally {
            setSaving(false);
        }
    };

    const applyToBatch = async () => {
        if (!plan?.batchKey) return;
        const req: PmBatchEditRequest = {};
        if (batchActive !== '') req.active = batchActive === 'true';
        if (batchPriority !== '') req.priority = batchPriority as WorkOrderPriority;
        if (batchLeadDays.trim() !== '' && num(batchLeadDays) != null) req.leadDays = num(batchLeadDays)!;
        if (batchAssignee) req.defaultAssignee = batchAssignee;
        if (batchTasks) req.tasks = tasks.map(t => t.trim()).filter(t => t !== '');
        if (Object.keys(req).length === 0) { toast.showError('Choose at least one setting to apply'); return; }
        try {
            setBatchSaving(true);
            const r = await MaintenanceService.batchEditPmPlans(plan.batchKey, req);
            toast.showSuccess(`${r.updated} plan(s) in this batch updated`);
        } catch (e) {
            toast.showApiError(e, 'Failed to update the batch');
        } finally {
            setBatchSaving(false);
        }
    };

    if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
    const ro = !canManage;

    return (
        <Box sx={{ p: 2 }} data-help-page="maint.pmForm.page">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" sx={{ mr: 1 }}>
                    {isEdit ? 'PM plan' : many ? 'Apply a PM plan to many machines' : 'New PM plan'}
                </Typography>
                {plan && <Chip size="small" label={PM_STATE_LABEL[plan.state]} color={PM_STATE_COLOR[plan.state]} />}
                {plan && pmDueHint(plan) && <Typography variant="caption">{pmDueHint(plan)}</Typography>}
                {plan?.openWorkOrderId && (
                    <Button size="small" onClick={() => navigate(`/maintenance/work-orders/${plan.openWorkOrderId}`)}>
                        Open order {plan.openWorkOrderNo}
                    </Button>
                )}
            </Box>

            {!isEdit && canManage && (
                <FormControlLabel
                    sx={{ mb: 1 }}
                    control={<Switch checked={many} onChange={e => { setMany(e.target.checked); setPreview(null); }} />}
                    label="Apply to many machines" data-help="maint.pmForm.field.many"
                />
            )}

            <Paper sx={{ p: 2 }} data-help-section="maint.pmForm.form">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <TextField size="small" label="Plan name" value={name} required data-help="maint.pmForm.field.name" disabled={ro} sx={field} onChange={e => setName(e.target.value)} />
                    {!many && (
                        <Box sx={field}>
                            <SearchableSelect
                                options={equipmentOptions} value={equipmentId} size="small" label="Machine" disabled={isEdit || ro} data-help="maint.pmForm.field.machine"
                                getOptionLabel={e => `${e.code} - ${e.name}`}
                                disableClientFilter onInputChange={searchEquipment}
                                onChange={v => { setEquipmentId(v); setMeterId(''); }}
                            />
                        </Box>
                    )}
                    {many && (
                        <>
                            <Box sx={field}>
                                <SearchableSelect
                                    options={types} value={typeId} size="small" label="Machine type (blank = all)" data-help="maint.pmForm.field.type"
                                    getOptionLabel={t => t.name} onChange={v => { setTypeId(v); setPreview(null); }}
                                />
                            </Box>
                            <Box sx={field}>
                                <SearchableSelect
                                    options={departments} value={locationId} size="small" label="Location (blank = all)" data-help="maint.pmForm.field.location"
                                    getOptionLabel={d => d.name} onChange={v => { setLocationId(v); setPreview(null); }}
                                />
                            </Box>
                            <TextField
                                size="small" type="number" label="Spread start dates over (days)" value={spreadDays} data-help="maint.pmForm.field.spread" sx={field}
                                onChange={e => setSpreadDays(e.target.value)}
                            />
                        </>
                    )}
                    <TextField select size="small" label="Trigger" data-help="maint.pmForm.field.trigger" value={trigger} disabled={ro} sx={field} onChange={e => setTrigger(e.target.value as PmTrigger)}>
                        {PM_TRIGGERS.map(t => <MenuItem key={t} value={t}>{t === 'CALENDAR_OR_METER' ? 'Calendar or meter' : mntLabel(t)}</MenuItem>)}
                    </TextField>
                    <TextField select size="small" label="Schedule basis" data-help="maint.pmForm.field.basis" value={basis} disabled={ro} sx={field} onChange={e => setBasis(e.target.value as PmBasis)}
                        helperText={basis === 'FIXED' ? 'Next due steps from the previous due date' : 'Next due counts from when the work was done'}>
                        {PM_BASES.map(b => <MenuItem key={b} value={b}>{mntLabel(b)}</MenuItem>)}
                    </TextField>
                    {usesCalendar && (
                        <>
                            <TextField size="small" type="number" label="Every" value={everyCount} data-help="maint.pmForm.field.everyCount" disabled={ro} sx={field} onChange={e => setEveryCount(e.target.value)} />
                            <TextField select size="small" label="Unit" value={everyUnit} data-help="maint.pmForm.field.everyUnit" disabled={ro} sx={field} onChange={e => setEveryUnit(e.target.value as CalendarUnit)}>
                                {CALENDAR_UNITS.map(u => <MenuItem key={u} value={u}>{mntLabel(u)}s</MenuItem>)}
                            </TextField>
                        </>
                    )}
                    {usesMeter && (
                        <>
                            {many ? (
                                <TextField size="small" label="Meter code on each machine" data-help="maint.pmForm.field.meterCode" value={meterCode} sx={field} onChange={e => setMeterCode(e.target.value)} />
                            ) : (
                                <Box sx={field}>
                                    <SearchableSelect
                                        options={meters} value={meterId} size="small" label="PM-driver meter" data-help="maint.pmForm.field.meter" disabled={ro}
                                        getOptionLabel={m => `${m.code} - ${m.name} (${m.unit ?? ''})`} onChange={v => setMeterId(v)}
                                    />
                                </Box>
                            )}
                            <TextField size="small" type="number" label="Every (meter units)" value={everyMeter} data-help="maint.pmForm.field.everyMeter" disabled={ro} sx={field} onChange={e => setEveryMeter(e.target.value)} />
                            <TextField size="small" type="number" label="Lead (meter units)" value={leadMeter} data-help="maint.pmForm.field.leadMeter" disabled={ro} sx={field} onChange={e => setLeadMeter(e.target.value)} />
                        </>
                    )}
                    <TextField size="small" type="number" label="Lead days" value={leadDays} data-help="maint.pmForm.field.leadDays" disabled={ro} sx={field} onChange={e => setLeadDays(e.target.value)} />
                    <TextField
                        size="small" type="date" label="Start date" data-help="maint.pmForm.field.startDate" value={startDate} disabled={ro} sx={field}
                        InputLabelProps={{ shrink: true }} onChange={e => setStartDate(e.target.value)}
                    />
                    <TextField select size="small" label="Priority" value={priority} data-help="maint.pmForm.field.priority" disabled={ro} sx={field} onChange={e => setPriority(e.target.value as WorkOrderPriority)}>
                        {WORK_ORDER_PRIORITIES.map(p => <MenuItem key={p} value={p}>{mntLabel(p)}</MenuItem>)}
                    </TextField>
                    <TextField size="small" type="number" label="Estimated minutes" data-help="maint.pmForm.field.estimatedMinutes" value={estimatedMinutes} disabled={ro} sx={field} onChange={e => setEstimatedMinutes(e.target.value)} />
                    <Box sx={field}>
                        <SearchableSelect
                            options={technicians} value={assignee} size="small" label="Default technician" disabled={ro} data-help="maint.pmForm.field.technician"
                            getOptionId={t => t.userId} getOptionLabel={t => t.name} onChange={v => setAssignee(v)}
                        />
                    </Box>
                    {isEdit && (
                        <FormControlLabel
                            control={<Switch checked={active} disabled={ro} onChange={e => setActive(e.target.checked)} />}
                            label="Active" sx={field} data-help="maint.pmForm.field.active"
                        />
                    )}
                </Box>

                <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>Checklist</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 700 }} data-help-section="maint.pmForm.section.checklist">
                    {tasks.map((t, i) => (
                        <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                                size="small" label={`Step ${i + 1}`} data-help="maint.pmForm.field.step" value={t} disabled={ro} sx={{ flex: '1 1 200px', minWidth: 0 }}
                                onChange={e => setTasks(tasks.map((x, j) => (j === i ? e.target.value : x)))}
                            />
                            {!ro && (
                                <IconButton
                                    aria-label={`Remove step ${i + 1}`} data-help="maint.pmForm.removeStep"
                                    onClick={() => setTasks(tasks.length === 1 ? [''] : tasks.filter((_, j) => j !== i))}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            )}
                        </Box>
                    ))}
                    {!ro && <Box><Button size="small" startIcon={<AddIcon />} onClick={() => setTasks([...tasks, ''])} data-help="maint.pmForm.addStep">Add step</Button></Box>}
                </Box>

                {many && preview && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        {preview.created} plan(s) would be created; {preview.skipped} machine(s) would be skipped (already
                        have a plan of this name, or no such meter).
                    </Alert>
                )}

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 3 }}>
                    <Button onClick={() => navigate('/maintenance/pm-plans')} data-help="maint.pmForm.cancel">{ro ? 'Back' : 'Cancel'}</Button>
                    {many && canManage && <Button data-help="maint.pmForm.preview" variant="outlined" disabled={saving} onClick={runPreview}>Preview</Button>}
                    {canManage && (
                        <Button data-help="maint.pmForm.save" variant="contained" disabled={saving} onClick={handleSave}>
                            {many ? 'Create plans' : 'Save'}
                        </Button>
                    )}
                </Box>
            </Paper>

            {isEdit && plan?.batchKey && canManage && (
                <Paper sx={{ p: 2, mt: 2 }} data-help-section="maint.pmForm.batch">
                    <Typography variant="subtitle1" sx={{ mb: 0.5 }}>Apply to all plans in this batch</Typography>
                    <Typography variant="caption" color="text.secondary">
                        This plan was created together with others. Only the settings you choose below change, on every plan
                        of the batch (machines that are retired or removed are left alone).
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                        <TextField select size="small" label="Active" value={batchActive} data-help="maint.pmForm.batch.active" sx={field} onChange={e => setBatchActive(e.target.value)}>
                            <MenuItem value="">No change</MenuItem>
                            <MenuItem value="true">Active</MenuItem>
                            <MenuItem value="false">Inactive</MenuItem>
                        </TextField>
                        <TextField select size="small" label="Priority" value={batchPriority} data-help="maint.pmForm.batch.priority" sx={field} onChange={e => setBatchPriority(e.target.value)}>
                            <MenuItem value="">No change</MenuItem>
                            {WORK_ORDER_PRIORITIES.map(p => <MenuItem key={p} value={p}>{mntLabel(p)}</MenuItem>)}
                        </TextField>
                        <TextField
                            size="small" type="number" label="Lead days (blank = no change)" data-help="maint.pmForm.batch.leadDays" value={batchLeadDays} sx={field}
                            onChange={e => setBatchLeadDays(e.target.value)}
                        />
                        <Box sx={field}>
                            <SearchableSelect
                                options={technicians} value={batchAssignee} size="small" label="Default technician (blank = no change)" data-help="maint.pmForm.batch.technician"
                                getOptionId={t => t.userId} getOptionLabel={t => t.name} onChange={v => setBatchAssignee(v)}
                            />
                        </Box>
                        <FormControlLabel
                            sx={field}
                            control={<Switch checked={batchTasks} onChange={e => setBatchTasks(e.target.checked)} />}
                            label="Replace every checklist with the one above" data-help="maint.pmForm.batch.tasks"
                        />
                    </Box>
                    <Box sx={{ mt: 2 }}>
                        <Button data-help="maint.pmForm.applyBatch" variant="outlined" disabled={batchSaving} onClick={applyToBatch}>Apply to all plans in this batch</Button>
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default PmPlanForm;
